import React, { useState } from 'react';
import {
  Modal,
  Button,
  Input,
  Typography,
  Loading,
  useToast,
  Form,
} from '@crisellevaldez/speculo-ui';
import { Search, PlusCircle, X, Trash, Edit2, Save } from 'lucide-react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useApprovedMLSList, ApprovedMLS } from '../../../../hooks/client/mls/useApprovedMLSList';
import {
  useAddApprovedMLS,
  useUpdateApprovedMLS,
  useDeleteApprovedMLS,
} from '../../../../hooks/client/mls/useApprovedMLSMutations';
import { ConfirmationDialog } from '../../../../components/common/dialogs/ConfirmationDialog';

interface ApprovedMLSListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApprovedMLSListModal: React.FC<ApprovedMLSListModalProps> = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebounce(searchInput, 300);

  // State for adding new MLS
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBoardCode, setNewBoardCode] = useState('');
  const [newMlsName, setNewMlsName] = useState('');
  const [newIsApproved, setNewIsApproved] = useState(true);

  // State for editing MLS
  const [editingBoardCode, setEditingBoardCode] = useState<string | null>(null);
  const [editMlsName, setEditMlsName] = useState('');
  const [editIsApproved, setEditIsApproved] = useState(false);

  // State for delete confirmation
  const [mlsToDelete, setMlsToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Hooks
  const { data: mlsList, isLoading } = useApprovedMLSList(isOpen);
  const { mutate: addMLS, isPending: isAdding } = useAddApprovedMLS();
  const { mutate: updateMLS, isPending: isUpdating } = useUpdateApprovedMLS();
  const { mutate: deleteMLS, isPending: isDeleting } = useDeleteApprovedMLS();

  // Filter MLS by search query (search both board code and MLS name)
  const filteredMLS = React.useMemo(() => {
    if (!mlsList) return [];
    if (!searchQuery) return mlsList;

    const query = searchQuery.toLowerCase();
    return mlsList.filter(
      (mls) =>
        mls.boardCode.toLowerCase().includes(query) || mls.mlsName.toLowerCase().includes(query)
    );
  }, [mlsList, searchQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleAddMLS = () => {
    if (!newBoardCode.trim()) {
      showToast({
        message: 'Board code is required',
        variant: 'error',
      });
      return;
    }

    // Check if MLS already exists
    const exists = mlsList?.some(
      (mls) => mls.boardCode.toLowerCase() === newBoardCode.trim().toLowerCase()
    );
    if (exists) {
      showToast({
        message: 'MLS with this board code already exists',
        variant: 'error',
      });
      return;
    }

    addMLS(
      {
        boardCode: newBoardCode.trim(),
        mlsName: newMlsName.trim(),
        isApproved: newIsApproved,
      },
      {
        onSuccess: () => {
          showToast({
            message: `MLS ${newBoardCode.trim()} has been added successfully`,
            variant: 'success',
          });
          setNewBoardCode('');
          setNewMlsName('');
          setNewIsApproved(true);
          setShowAddForm(false);
        },
        onError: (error) => {
          showToast({
            message: error instanceof Error ? error.message : 'Failed to add MLS',
            variant: 'error',
          });
        },
      }
    );
  };

  const handleStartEdit = (mls: ApprovedMLS) => {
    setEditingBoardCode(mls.boardCode);
    setEditMlsName(mls.mlsName);
    setEditIsApproved(mls.isApproved);
  };

  const handleCancelEdit = () => {
    setEditingBoardCode(null);
    setEditMlsName('');
    setEditIsApproved(false);
  };

  const handleSaveEdit = () => {
    if (!editingBoardCode) return;

    updateMLS(
      {
        boardCode: editingBoardCode,
        mlsName: editMlsName,
        isApproved: editIsApproved,
      },
      {
        onSuccess: () => {
          showToast({
            message: `MLS ${editingBoardCode} has been updated successfully`,
            variant: 'success',
          });
          setEditingBoardCode(null);
          setEditMlsName('');
          setEditIsApproved(false);
        },
        onError: (error) => {
          showToast({
            message: error instanceof Error ? error.message : 'Failed to update MLS',
            variant: 'error',
          });
        },
      }
    );
  };

  const handleDeleteClick = (boardCode: string) => {
    setMlsToDelete(boardCode);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!mlsToDelete) return;

    deleteMLS(mlsToDelete, {
      onSuccess: () => {
        showToast({
          message: `MLS ${mlsToDelete} has been deleted successfully`,
          variant: 'success',
        });
        setShowDeleteConfirm(false);
        setMlsToDelete(null);
      },
      onError: (error) => {
        showToast({
          message: error instanceof Error ? error.message : 'Failed to delete MLS',
          variant: 'error',
        });
        setShowDeleteConfirm(false);
        setMlsToDelete(null);
      },
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setMlsToDelete(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h3"> MLS List</Typography>
        </Modal.Header>

        <Modal.Body className="py-6">
          <div className="space-y-4">
            {/* Header with search and add button */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  placeholder="Search by board code..."
                  value={searchInput}
                  onChange={handleSearch}
                />
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 whitespace-nowrap h-9"
                disabled={showAddForm}
              >
                <PlusCircle className="h-4 w-4" />
                Add MLS
              </Button>
            </div>

            {/* Add new MLS form */}
            {showAddForm && (
              <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4 bg-gray-100 dark:bg-white/5">
                <h4 className="text-sm font-semibold mb-3 text-foreground">Add New MLS</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Form.Control>
                    <Form.Label>Board Code</Form.Label>
                    <Input
                      placeholder="e.g., CRMLS"
                      value={newBoardCode}
                      onChange={(e) => setNewBoardCode(e.target.value)}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Form.Label>MLS Name</Form.Label>
                    <Input
                      placeholder="e.g., California Regional MLS"
                      value={newMlsName}
                      onChange={(e) => setNewMlsName(e.target.value)}
                    />
                  </Form.Control>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newIsApproved}
                      onChange={(e) => setNewIsApproved(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-white/15 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm font-medium text-foreground">Approved</span>
                  </label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewBoardCode('');
                        setNewMlsName('');
                        setNewIsApproved(true);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddMLS}
                      isLoading={isAdding}
                      disabled={isAdding || !newBoardCode.trim()}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* MLS List */}
            <div className="h-[400px] overflow-y-auto">
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <Loading />
                </div>
              ) : filteredMLS.length === 0 ? (
                <div className="flex justify-center items-center h-full">
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'No MLS found matching your search' : 'No MLS entries found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredMLS.map((mls) => (
                    <div
                      key={mls.boardCode}
                      className={`border rounded-lg p-3 transition-colors ${
                        mls.isApproved
                          ? 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/30'
                          : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/10'
                      }`}
                    >
                      {editingBoardCode === mls.boardCode ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                Board Code
                              </label>
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {mls.boardCode}
                              </p>
                            </div>
                            <Form.Control>
                              <Form.Label className="text-xs">MLS Name</Form.Label>
                              <Input
                                value={editMlsName}
                                onChange={(e) => setEditMlsName(e.target.value)}
                                placeholder="Enter MLS name"
                              />
                            </Form.Control>
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editIsApproved}
                                onChange={(e) => setEditIsApproved(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 dark:border-white/15 text-green-600 focus:ring-green-500"
                              />
                              <span className="text-sm font-medium text-foreground">Approved</span>
                            </label>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancelEdit}
                                className="flex items-center gap-1 h-8"
                              >
                                <X className="w-3 h-3" />
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={handleSaveEdit}
                                isLoading={isUpdating}
                                disabled={isUpdating}
                                className="flex items-center gap-1 h-8"
                              >
                                <Save className="w-3 h-3" />
                                Save
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Board Code and Name */}
                            <div>
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                {mls.boardCode}
                              </h3>
                              {mls.mlsName && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {mls.mlsName}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStartEdit(mls)}
                              className="flex items-center gap-1 h-8"
                            >
                              <Edit2 className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(mls.boardCode)}
                              className="flex items-center gap-1 h-8 !bg-red-600 hover:!bg-red-400 !text-white !border-red-600"
                            >
                              <Trash className="w-3 h-3" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer info */}
            <div className="text-sm text-gray-500 dark:text-gray-400 pt-2 border-t dark:border-white/10">
              Total: {mlsList?.length || 0} MLS entries | Approved:{' '}
              {mlsList?.filter((m) => m.isApproved).length || 0} | Not Approved:{' '}
              {mlsList?.filter((m) => !m.isApproved).length || 0}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete MLS"
        message={`Are you sure you want to delete ${mlsToDelete}? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
};
